import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Logo } from '../components/Logo';
import { 
  ShoppingBag, 
  Trash2, 
  Plus, 
  Minus, 
  ArrowRight,
  ArrowLeft,
  Truck,
  Shield,
  CreditCard,
  Loader2,
  ShoppingCart,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const CartPage = () => {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' or 'affirm'
  
  // Shipping form
  const [shippingForm, setShippingForm] = useState({
    first_name: '',
    last_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchCart = async () => {
    try {
      const response = await api.get('/cart/');
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    setUpdating(true);
    try {
      if (quantity <= 0) {
        await api.delete(`/cart/remove/${productId}`);
      } else {
        await api.put(`/cart/update/${productId}?quantity=${quantity}`);
      }
      await fetchCart();
      toast.success('Cart updated');
    } catch (error) {
      toast.error('Failed to update cart');
    } finally {
      setUpdating(false);
    }
  };

  const removeItem = async (productId) => {
    await updateQuantity(productId, 0);
  };

  const handleCheckout = async () => {
    // Validate shipping form
    const required = ['first_name', 'last_name', 'address_line1', 'city', 'state', 'postal_code'];
    const missing = required.filter(field => !shippingForm[field]);
    
    if (missing.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCheckingOut(true);
    try {
      const response = await api.post('/orders/checkout', {
        shipping_address: shippingForm,
        use_affirm: paymentMethod === 'affirm'
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error('Failed to create checkout session');
      console.error('Checkout error:', error);
    } finally {
      setCheckingOut(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#090D0F]">
        <header className="sticky top-0 z-50 glass border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Logo linkTo="/" size="default" />
          </div>
        </header>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <ShoppingCart className="w-16 h-16 text-zinc-700 mx-auto mb-6" />
          <h1 className="text-3xl font-bold mb-4">Sign In to View Cart</h1>
          <p className="text-zinc-500 mb-8">Please sign in to access your shopping cart</p>
          <Link to="/login">
            <Button className="bg-teal-400 text-black font-bold uppercase hover:bg-teal-300">
              Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090D0F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }

  const isEmpty = !cart || cart.items.length === 0;

  return (
    <div className="min-h-screen bg-[#090D0F]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo="/" size="default" />
          <div className="flex items-center gap-4">
            <Link to="/shop">
              <Button variant="ghost" className="text-zinc-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Continue Shopping
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-8">YOUR CART</h1>

        {isEmpty ? (
          <div className="text-center py-20">
            <ShoppingBag className="w-16 h-16 text-zinc-700 mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-4">Your cart is empty</h2>
            <p className="text-zinc-500 mb-8">Add a Lagree machine to get started</p>
            <Link to="/shop">
              <Button className="bg-teal-400 text-black font-bold uppercase hover:bg-teal-300">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Shop Machines
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="space-y-6">
                {cart.items.map((item) => (
                  <div 
                    key={item.product_id}
                    className="bg-[#121416] border border-zinc-800 rounded-sm p-6 flex gap-6"
                  >
                    <div className="w-32 h-32 bg-zinc-800 rounded-sm overflow-hidden flex-shrink-0">
                      <img 
                        src="https://images.unsplash.com/photo-1638805981949-362f5964521e?q=85&w=200"
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.name}</h3>
                      <p className="text-teal-400 text-lg font-semibold mb-4">
                        ${item.price.toLocaleString()}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center border border-zinc-700 rounded-sm">
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                            disabled={updating}
                            className="p-2 hover:bg-zinc-800 transition-colors disabled:opacity-50"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="px-4 font-medium">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                            disabled={updating}
                            className="p-2 hover:bg-zinc-800 transition-colors disabled:opacity-50"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(item.product_id)}
                          disabled={updating}
                          className="text-red-400 hover:text-red-300 p-2"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Shipping Form */}
              {showCheckout && (
                <div className="mt-8 bg-[#121416] border border-zinc-800 rounded-sm p-6">
                  <h3 className="text-xl font-bold mb-6">SHIPPING ADDRESS</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400">First Name *</Label>
                      <Input
                        value={shippingForm.first_name}
                        onChange={(e) => setShippingForm({...shippingForm, first_name: e.target.value})}
                        className="bg-[#090D0F] border-zinc-700 mt-1"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Last Name *</Label>
                      <Input
                        value={shippingForm.last_name}
                        onChange={(e) => setShippingForm({...shippingForm, last_name: e.target.value})}
                        className="bg-[#090D0F] border-zinc-700 mt-1"
                        placeholder="Doe"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-zinc-400">Address Line 1 *</Label>
                      <Input
                        value={shippingForm.address_line1}
                        onChange={(e) => setShippingForm({...shippingForm, address_line1: e.target.value})}
                        className="bg-[#090D0F] border-zinc-700 mt-1"
                        placeholder="123 Main St"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-zinc-400">Address Line 2</Label>
                      <Input
                        value={shippingForm.address_line2}
                        onChange={(e) => setShippingForm({...shippingForm, address_line2: e.target.value})}
                        className="bg-[#090D0F] border-zinc-700 mt-1"
                        placeholder="Apt 4B"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">City *</Label>
                      <Input
                        value={shippingForm.city}
                        onChange={(e) => setShippingForm({...shippingForm, city: e.target.value})}
                        className="bg-[#090D0F] border-zinc-700 mt-1"
                        placeholder="Los Angeles"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">State *</Label>
                      <Input
                        value={shippingForm.state}
                        onChange={(e) => setShippingForm({...shippingForm, state: e.target.value})}
                        className="bg-[#090D0F] border-zinc-700 mt-1"
                        placeholder="CA"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">ZIP Code *</Label>
                      <Input
                        value={shippingForm.postal_code}
                        onChange={(e) => setShippingForm({...shippingForm, postal_code: e.target.value})}
                        className="bg-[#090D0F] border-zinc-700 mt-1"
                        placeholder="90001"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Phone</Label>
                      <Input
                        value={shippingForm.phone}
                        onChange={(e) => setShippingForm({...shippingForm, phone: e.target.value})}
                        className="bg-[#090D0F] border-zinc-700 mt-1"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div>
              <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6 sticky top-24">
                <h3 className="text-xl font-bold mb-6">ORDER SUMMARY</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-zinc-400">
                    <span>Subtotal</span>
                    <span className="text-white">${cart.subtotal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between text-zinc-400">
                    <span>Tax (8%)</span>
                    <span className="text-white">${cart.tax.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between text-zinc-400">
                    <span>Shipping</span>
                    <span className="text-white">
                      {cart.shipping === 0 ? (
                        <span className="text-teal-400">FREE</span>
                      ) : (
                        `$${cart.shipping}`
                      )}
                    </span>
                  </div>
                  <div className="border-t border-zinc-700 pt-4 flex justify-between">
                    <span className="text-lg font-bold">Total</span>
                    <span className="text-2xl font-bold">${cart.total.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                </div>

                {/* Financing Option */}
                <div className="space-y-3 mb-6">
                  <p className="text-sm font-medium text-zinc-400">Payment Method</p>
                  
                  {/* Stripe Option */}
                  <button
                    onClick={() => setPaymentMethod('stripe')}
                    className={`w-full p-4 rounded-sm border text-left transition-colors ${
                      paymentMethod === 'stripe' 
                        ? 'border-teal-400 bg-teal-400/10' 
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-5 h-5 text-teal-400" />
                        <div>
                          <span className="font-medium">Pay in Full</span>
                          <p className="text-xs text-zinc-500">Credit card or debit card</p>
                        </div>
                      </div>
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        paymentMethod === 'stripe' ? 'border-teal-400 bg-teal-400' : 'border-zinc-600'
                      }`} />
                    </div>
                  </button>

                  {/* Affirm Option */}
                  <button
                    onClick={() => setPaymentMethod('affirm')}
                    className={`w-full p-4 rounded-sm border text-left transition-colors ${
                      paymentMethod === 'affirm' 
                        ? 'border-teal-400 bg-teal-400/10' 
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 bg-blue-500 rounded flex items-center justify-center text-xs font-bold text-white">A</div>
                        <div>
                          <span className="font-medium">Pay with Affirm</span>
                          <p className="text-xs text-zinc-500">
                            As low as <span className="text-teal-400 font-bold">${Math.round(cart.subtotal / 30)}/mo</span> • 0% APR available
                          </p>
                        </div>
                      </div>
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        paymentMethod === 'affirm' ? 'border-teal-400 bg-teal-400' : 'border-zinc-600'
                      }`} />
                    </div>
                  </button>
                  
                  {paymentMethod === 'affirm' && (
                    <p className="text-xs text-zinc-500 px-1">
                      Select Affirm at checkout to split your purchase into easy monthly payments. Subject to credit check.
                    </p>
                  )}
                </div>

                {showCheckout ? (
                  <Button
                    onClick={handleCheckout}
                    disabled={checkingOut}
                    className="w-full h-14 bg-teal-400 text-black font-bold uppercase hover:bg-teal-300"
                    data-testid="place-order-btn"
                  >
                    {checkingOut ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Place Order
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => setShowCheckout(true)}
                    className="w-full h-14 bg-teal-400 text-black font-bold uppercase hover:bg-teal-300"
                    data-testid="checkout-btn"
                  >
                    Proceed to Checkout
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                )}

                {/* Benefits */}
                <div className="mt-6 space-y-3 text-sm">
                  <div className="flex items-center gap-3 text-zinc-400">
                    <Truck className="w-5 h-5 text-teal-400" />
                    <span>Free shipping on orders over $500</span>
                  </div>
                  <div className="flex items-center gap-3 text-zinc-400">
                    <Shield className="w-5 h-5 text-teal-400" />
                    <span>1-2 year manufacturer warranty</span>
                  </div>
                  <div className="flex items-center gap-3 text-zinc-400">
                    <Check className="w-5 h-5 text-teal-400" />
                    <span>30-day satisfaction guarantee</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CartPage;
